import React from 'react';
import { NavLink } from 'react-router-dom';
import { BarChart2, MessageSquare, CreditCard, Settings, Key, Database, Plug, X, Shield, Users, Building, Home, Activity, BarChart3, Sparkles, LifeBuoy } from 'lucide-react';
import ShinyText from '../ui/ShinyText';
import { useAuth } from '../../hooks/useAuth';
import { useImpersonation } from '../../context/ImpersonationContext';

const iconMap = {
  Dashboard: BarChart2,
  Conversations: MessageSquare,
  Subscriptions: CreditCard,
  Configuration: Settings,
  'API Keys': Key,
  'Data Management': Database,
  Integrations: Plug,
  'AI Configuration': Sparkles,
  Tenants: Building,
  'All Users': Users,
  'All Subscriptions': CreditCard,
  'Admin Home': Home,
  'System Stats': Activity,
  'System Analytics': BarChart3,
  'All API Keys': Key,
  Support: LifeBuoy,
};

// Tenant-specific nav items (shown when managing a tenant or for regular users)
const tenantNavItems = [
  { name: 'Dashboard', path: '/dashboard' },
  { name: 'Conversations', path: '/conversations' },
  { name: 'Integrations', path: '/integrations' },
  { name: 'Subscriptions', path: '/subscriptions' },
  { name: 'Configuration', path: '/configuration' },
  { name: 'API Keys', path: '/api-keys' },
  { name: 'Data Management', path: '/data-management' },
  { name: 'Support', path: '/support' },
];

// Super admin only nav items (shown only to super_admin when managing a tenant)
const superAdminTenantNavItems = [
  { name: 'AI Configuration', path: '/ai-configuration' },
];

// Admin-only nav items (only visible to super_admin)
const adminNavItems = [
  { name: 'Admin Home', path: '/admin/dashboard' },
  { name: 'Tenants', path: '/admin/tenants' },
  { name: 'All Users', path: '/admin/users' },
  { name: 'All Subscriptions', path: '/admin/subscriptions' },
  { name: 'System Analytics', path: '/admin/system-stats' },
  { name: 'All API Keys', path: '/admin/api-keys' },
];

interface SidebarProps {
  isOpen?: boolean;
  onClose?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen = true, onClose }) => {
  const { user } = useAuth();
  const { isImpersonating, actingTenantName } = useImpersonation();
  const isSuperAdmin = user?.type === 'super_admin';
  
  // Super admin sees tenant pages only when impersonating
  // Regular users always see tenant pages
  const showTenantNav = !isSuperAdmin || isImpersonating;

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && onClose && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-[70] lg:hidden"
          onClick={onClose}
        />
      )}
      
      {/* Sidebar */}
      <aside className={`
        w-64 flex-shrink-0 dark:bg-surface bg-light-surface border-r dark:border-border-color border-light-border-color flex flex-col
        fixed lg:static inset-y-0 left-0 z-[80]
        transform transition-transform duration-300 ease-in-out
        ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="h-16 flex items-center justify-between px-4 border-b dark:border-border-color border-light-border-color">
          <ShinyText 
            text="Mentoros" 
            disabled={false} 
            speed={5} 
            className="text-3xl font-bold dark:text-white text-gray-900"
          />
          {/* Mobile close button */}
          {onClose && (
            <button
              onClick={onClose}
              className="lg:hidden p-2 rounded-lg dark:hover:bg-gray-700 hover:bg-gray-100 transition-colors"
              aria-label="Close menu"
            >
              <X className="w-6 h-6 dark:text-white text-gray-900" />
            </button>
          )}
        </div>
        <nav className="flex-1 px-4 py-6 space-y-2 overflow-y-auto">
          {/* Admin Section - Only visible to super_admin at the top */}
          {isSuperAdmin && (
            <>
              <div className="pb-2">
                <div className="flex items-center gap-2 px-4 text-xs font-semibold uppercase tracking-wider dark:text-gray-500 text-gray-600">
                  <Shield className="w-4 h-4" />
                  <span>Admin</span>
                </div>
              </div>
              {adminNavItems.map((item) => {
                const Icon = iconMap[item.name as keyof typeof iconMap] || Shield;
                return (
                  <NavLink
                    key={item.name}
                    to={item.path}
                    onClick={() => onClose?.()}
                    className={({ isActive }) =>
                      `flex items-center px-4 py-2.5 text-sm font-medium rounded-lg transition-colors duration-200 ${
                        isActive
                          ? 'bg-primary text-white'
                          : 'dark:text-text-secondary text-light-text-secondary dark:hover:bg-gray-700 hover:bg-gray-100 dark:hover:text-white hover:text-gray-900'
                      }`
                    }
                  >
                    <Icon className="w-5 h-5 mr-3" />
                    {item.name}
                  </NavLink>
                );
              })}
            </>
          )}

          {/* Tenant Section - For regular users always, for super_admin only when impersonating */}
          {showTenantNav && (
            <>
              {isSuperAdmin && (
                <div className="pt-6 pb-2">
                  <div className="flex items-center gap-2 px-4 text-xs font-semibold uppercase tracking-wider dark:text-gray-500 text-gray-600">
                    <Building className="w-4 h-4" />
                    <span className="truncate">{actingTenantName || 'Tenant'}</span>
                  </div>
                </div>
              )}
              {tenantNavItems.map((item) => {
                const Icon = iconMap[item.name as keyof typeof iconMap] || BarChart2;
                return (
                  <NavLink
                    key={item.name}
                    to={item.path}
                    end={item.path === '/'}
                    onClick={() => onClose?.()}
                    className={({ isActive }) =>
                      `flex items-center px-4 py-2.5 text-sm font-medium rounded-lg transition-colors duration-200 ${
                        isActive
                          ? 'bg-primary text-white'
                          : 'dark:text-text-secondary text-light-text-secondary dark:hover:bg-gray-700 hover:bg-gray-100 dark:hover:text-white hover:text-gray-900'
                      }`
                    }
                  >
                    <Icon className="w-5 h-5 mr-3" />
                    {item.name}
                  </NavLink>
                );
              })}
              
              {/* Super Admin only tenant items - Only visible to super_admin when impersonating */}
              {isSuperAdmin && superAdminTenantNavItems.map((item) => {
                const Icon = iconMap[item.name as keyof typeof iconMap] || BarChart2;
                return (
                  <NavLink
                    key={item.name}
                    to={item.path}
                    end={item.path === '/'}
                    onClick={() => onClose?.()}
                    className={({ isActive }) =>
                      `flex items-center px-4 py-2.5 text-sm font-medium rounded-lg transition-colors duration-200 ${
                        isActive
                          ? 'bg-primary text-white'
                          : 'dark:text-text-secondary text-light-text-secondary dark:hover:bg-gray-700 hover:bg-gray-100 dark:hover:text-white hover:text-gray-900'
                      }`
                    }
                  >
                    <Icon className="w-5 h-5 mr-3" />
                    {item.name}
                  </NavLink>
                );
              })}
            </>
          )}
        </nav>
      </aside>
    </>
  );
};

export default Sidebar;
